import React, {useCallback, useEffect, useState} from "react";
import {connect} from "react-redux";
import {Badge, Button, Modal, ModalContent, styled} from "@reside/ui";
import {PatchAdmissionDto} from "@reside/reside-api-admission";

import {select} from "../../store";
import {SimpleFacilityModel} from "../../utils/types/facility";
import {AdmissionFromSearch} from "../../services/AdmissionsService";
import {WorkflowList} from "../start-workflow/WorkflowList";
import {WorkflowOption} from "../start-workflow/types";
import {admissionOnlyWorkflowOption} from "../start-workflow/workflowMapping";
import {useFacilityWorkflowsQuery} from "../start-workflow/hooks/useFacilityWorkflowsQuery";
import {useSetAdmissionWorkflow} from "./useSetAdmissionWorkflow";

type StateProps = Readonly<{
  facilities: ReadonlyArray<SimpleFacilityModel>;
}>;

type OwnProps = Readonly<{
  admission: AdmissionFromSearch;
  isOpen: boolean;
  onRequestClose: () => void;
  onSuccess?: () => void;
}>;

const SetWorkflowModalRenderer = ({
  facilities,
  admission,
  isOpen,
  onRequestClose,
  onSuccess,
}: OwnProps & StateProps) => {
  const facility = facilities.find(({id}) => id === admission.facilityId);
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowOption>(
    admissionOnlyWorkflowOption,
  );
  const {isSubmitting, setWorkflow} = useSetAdmissionWorkflow(onSuccess);
  const {isLoading: isLoadingWorkflows} = useFacilityWorkflowsQuery(
    admission.facilityId,
    admission.templateId,
    {enabled: isOpen && Boolean(admission.facilityId)},
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedWorkflow(admissionOnlyWorkflowOption());
    }
  }, [isOpen]);

  const handleSetWorkflow = useCallback(async () => {
    const success = await setWorkflow(
      admission.id,
      selectedWorkflow.type as PatchAdmissionDto.WorkflowTypeEnum,
    );

    if (success) {
      onRequestClose();
    }
  }, [admission.id, onRequestClose, selectedWorkflow, setWorkflow]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      closeButtonPosition="right"
      title="Set workflow"
      subtitle="Select a workflow to continue."
      Content={StyledModalContent}
    >
      {({closeModal, Content, FooterButtons}) => (
        <>
          <Content data-test-id="setWorkflowModal">
            <StepHeader>
              <StepBadge color="lightBlue10">1</StepBadge>
              <StepBadgeText>FACILITY</StepBadgeText>
            </StepHeader>
            <FacilityHeader>
              <FacilityName data-test-id="setWorkflowFacilityName">
                {facility?.name}
              </FacilityName>
            </FacilityHeader>
            <WorkflowSection>
              <StepHeader>
                <StepBadge color="lightBlue10">2</StepBadge>
                <StepBadgeText>WORKFLOW</StepBadgeText>
              </StepHeader>
              <WorkflowList
                facilityId={admission.facilityId}
                templateId={admission.templateId}
                selectedWorkflow={selectedWorkflow}
                onSelect={setSelectedWorkflow}
              />
            </WorkflowSection>
          </Content>
          <FooterButtons>
            <Button
              color="primary"
              outline
              onClick={closeModal}
              testId="setWorkflowCancel"
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={isSubmitting || isLoadingWorkflows}
              onClick={handleSetWorkflow}
              testId="setWorkflowSubmit"
            >
              Set workflow
            </Button>
          </FooterButtons>
        </>
      )}
    </Modal>
  );
};

const mapStateToProps = (state: any): StateProps => ({
  facilities: select.adminSession.facilities(state),
});

export const SetWorkflowModal = connect(mapStateToProps)(
  SetWorkflowModalRenderer,
);

const StyledModalContent = styled(ModalContent)`
  overflow: visible;
  color: ${({theme}) => theme.color.darkBlue100};
`;

const FacilityHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({theme}) => theme.space[3]}px;
  margin-bottom: ${({theme}) => theme.space[4]}px;
  margin-top: ${({theme}) => theme.space[4]}px;
`;

const WorkflowSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({theme}) => theme.space[4]}px;
  margin-top: ${({theme}) => theme.space[6]}px;
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({theme}) => theme.space[2]}px;
`;

const FacilityName = styled.span`
  min-width: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({theme}) => theme.color.gray100};
`;

const StepBadge = styled(Badge)`
  color: ${({theme}) => theme.color.darkBlue70};
  font-weight: 600;
  background-color: ${({theme}) => theme.color.lightBlue10};
  margin-left: ${({theme}) => theme.space[1]}px;
`;

const StepBadgeText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({theme}) => theme.color.darkBlue70};
`;
